/* ShelfKnight glyph portraits.
   Each [data-sk-glyph] figure holds a portrait <img> and an empty <canvas>.
   On hover (mouse) or on its toggle button (any pointer), the portrait is
   reprinted on the canvas as a grid of characters taken, in order, from the
   author's own words (data-text). Brightness picks the glyph colour
   (--glyph-0..7) and size (dim cells at 62%). It resolves row by row over
   about 1.5 s, and cells under the cursor flare to the accent, then settle.
   Reduced motion: the finished picture, no resolve, no flare.
   The image is read with CORS (Shopify's CDN sends Access-Control-Allow-
   Origin: *); if the pixels cannot be read, the photograph simply stays. */
(() => {
  const RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  const NOISE = '+-|/\\=:;.~*#';
  const ASPECT = 1.85;

  function palette(el) {
    const cs = getComputedStyle(el);
    const g = [];
    for (let i = 0; i < 8; i++) g.push(cs.getPropertyValue(`--glyph-${i}`).trim() || '#5f8f5a');
    return { g, accent: cs.getPropertyValue('--accent').trim() || '#c6ffb0' };
  }

  function levels(img, cols) {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const rows = Math.max(1, Math.round((cols * h) / w / ASPECT));
    const c = document.createElement('canvas');
    c.width = cols;
    c.height = rows;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(img, 0, 0, cols, rows);
    const d = x.getImageData(0, 0, cols, rows).data; // throws if tainted
    const raw = new Float32Array(cols * rows);
    for (let i = 0; i < cols * rows; i++) raw[i] = 0.2126 * d[i * 4] + 0.7152 * d[i * 4 + 1] + 0.0722 * d[i * 4 + 2];
    // Local contrast: add back the difference from a 5x3-cell blur, so the
    // shadows of eyes, nose and folds read in a grid this coarse.
    const lum = new Float32Array(cols * rows);
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        let sum = 0;
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const xx = x + dx;
            const yy = y + dy;
            if (xx < 0 || yy < 0 || xx >= cols || yy >= rows) continue;
            sum += raw[yy * cols + xx];
            n++;
          }
        }
        const v = raw[y * cols + x];
        lum[y * cols + x] = v < 24 ? v : v + 1.6 * (v - sum / n);
      }
    }
    // The portraits sit on a flat dark ground. Cells near the ground stay
    // blank; the rest are spread over the 8 levels by their own range (5th
    // to 98th percentile), so a pale bust keeps its eyes and folds.
    const sorted = Array.from(lum).sort((x, y) => x - y);
    const ground = Math.max(24, sorted[Math.floor(sorted.length * 0.02)] + 10);
    const subject = sorted.filter((v) => v > ground);
    const lv = new Uint8Array(cols * rows);
    if (!subject.length) return { lv, cols, rows };
    const lo = subject[Math.floor(subject.length * 0.05)];
    const hi = subject[Math.floor(subject.length * 0.98)];
    const span = Math.max(1, hi - lo);
    for (let i = 0; i < lum.length; i++) {
      if (lum[i] <= ground) continue;
      const n = Math.min(1, Math.max(0, (lum[i] - lo) / span));
      lv[i] = 1 + Math.min(7, Math.floor(n * 8));
    }
    return { lv, cols, rows };
  }

  class Glyph {
    constructor(fig) {
      this.fig = fig;
      this.img = fig.querySelector('img');
      this.cv = fig.querySelector('canvas');
      this.btn = fig.querySelector('[data-sk-glyph-toggle]');
      this.text = (fig.dataset.text || '').replace(/\s+/g, ' ').trim() || 'ShelfKnight';
      this.colsWanted = parseInt(fig.dataset.cols || '96', 10);
      this.ready = false;
      this.failed = false;
      this.on = false;
      this.pinned = false;
      this.heat = null;
      this.raf = 0;
      this.bind();
    }

    bind() {
      if (this.btn) {
        this.btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.pinned = !this.pinned;
          this.btn.setAttribute('aria-pressed', String(this.pinned));
          this.set(this.pinned);
        });
      }
      const hoverTarget = this.fig.closest('[data-sk-glyph-hover]') || this.fig;
      hoverTarget.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') this.set(true);
      });
      hoverTarget.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse' && !this.pinned) this.set(false);
      });
      hoverTarget.addEventListener('focusin', () => this.set(true));
      hoverTarget.addEventListener('focusout', () => { if (!this.pinned) this.set(false); });
      this.cv.addEventListener('pointermove', (e) => this.flare(e));
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(() => { if (this.ready) { this.layout(); this.draw(1); } }, 150);
      });
    }

    prepare() {
      if (this.ready || this.failed) return this.ready;
      if (!this.img.complete || !this.img.naturalWidth) return false;
      try {
        this.pal = palette(this.fig);
        this.grid = levels(this.img, this.colsWanted);
        this.chars = [];
        let k = 0;
        const t = this.text;
        for (let i = 0; i < this.grid.lv.length; i++) {
          if (this.grid.lv[i] === 0) { this.chars.push(''); continue; }
          let ch = t[k % t.length];
          k++;
          if (ch === ' ') { ch = t[k % t.length]; k++; }
          this.chars.push(ch);
        }
        this.heat = new Float32Array(this.grid.lv.length);
        this.layout();
        this.ready = true;
      } catch (err) {
        this.failed = true;
        this.fig.classList.add('is-glyph-failed');
      }
      return this.ready;
    }

    layout() {
      const r = this.cv.getBoundingClientRect();
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      this.cv.width = Math.max(1, Math.round(r.width * dpr));
      this.cv.height = Math.max(1, Math.round(r.height * dpr));
      this.cw = this.cv.width / this.grid.cols;
      this.ch = this.cv.height / this.grid.rows;
      this.font = getComputedStyle(this.fig).getPropertyValue('--glyph-font').trim() || 'ui-monospace, monospace';
    }

    set(on) {
      if (on === this.on) return;
      if (on && !this.prepare()) {
        if (!this.failed && !this.img.complete) this.img.addEventListener('load', () => this.on && this.start(), { once: true });
        this.on = true;
        return;
      }
      this.on = on;
      this.fig.classList.toggle('is-glyph', on);
      if (on) this.start();
    }

    start() {
      if (!this.prepare()) return;
      this.fig.classList.add('is-glyph');
      if (RM.matches) { this.draw(1); return; }
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - t0) / 1500);
        this.draw(p);
        if (p < 1 && this.on) this.raf = requestAnimationFrame(step);
      };
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(step);
    }

    draw(p, flaring) {
      const { lv, cols, rows } = this.grid;
      const x = this.cv.getContext('2d');
      x.clearRect(0, 0, this.cv.width, this.cv.height);
      x.textAlign = 'center';
      x.textBaseline = 'middle';
      const doneRows = Math.floor(p * rows);
      const big = `500 ${Math.round(this.ch * 0.92)}px ${this.font}`;
      const small = `500 ${Math.round(this.ch * 0.92 * 0.62)}px ${this.font}`;
      let cur = '';
      for (let r = 0; r < rows; r++) {
        if (r > doneRows + 2) break;
        const noisy = r > doneRows;
        for (let c = 0; c < cols; c++) {
          const i = r * cols + c;
          let l = lv[i];
          if (!l) continue;
          let ch = this.chars[i];
          const h = this.heat[i];
          if (noisy) {
            ch = NOISE[(i * 7 + r) % NOISE.length];
            l = 1 + ((i * 13) % 4);
          }
          const f = h > 0.05 ? big : l <= 3 ? small : big;
          if (f !== cur) { x.font = f; cur = f; }
          x.fillStyle = h > 0.05 ? this.pal.accent : this.pal.g[Math.min(7, l - 1)];
          x.fillText(ch, (c + 0.5) * this.cw, (r + 0.5) * this.ch);
        }
      }
    }

    flare(e) {
      if (!this.on || !this.ready || RM.matches) return;
      const r = this.cv.getBoundingClientRect();
      const cx = ((e.clientX - r.left) / r.width) * this.grid.cols;
      const cy = ((e.clientY - r.top) / r.height) * this.grid.rows;
      const rad = 3.2;
      for (let y = Math.floor(cy - rad); y <= cy + rad; y++) {
        for (let x = Math.floor(cx - rad * 1.8); x <= cx + rad * 1.8; x++) {
          if (x < 0 || y < 0 || x >= this.grid.cols || y >= this.grid.rows) continue;
          const dx = (x - cx) / 1.8;
          const dy = y - cy;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < rad) this.heat[y * this.grid.cols + x] = Math.max(this.heat[y * this.grid.cols + x], 1 - d / rad);
        }
      }
      if (!this.cooling) this.cool();
    }

    cool() {
      this.cooling = true;
      const tick = () => {
        let any = false;
        for (let i = 0; i < this.heat.length; i++) {
          if (this.heat[i] > 0) { this.heat[i] = Math.max(0, this.heat[i] - 0.06); any = true; }
        }
        this.draw(1);
        if (any && this.on) requestAnimationFrame(tick);
        else this.cooling = false;
      };
      requestAnimationFrame(tick);
    }
  }

  const init = () => document.querySelectorAll('[data-sk-glyph]').forEach((f) => {
    if (!f.skGlyph) f.skGlyph = new Glyph(f);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
